import { useState, useMemo, useEffect } from "react";
import "./App.css";
import {
  Search, TrendingUp, CheckCircle2, Star, Library,
  Trophy, BarChart3, Home, LogOut, Compass, SlidersHorizontal, ListPlus, Sparkles,
  Settings, X, BookOpen, Users, Eye,
  ScanLine,
} from "lucide-react";
import { TopNav } from "./components/TopNav";
import { OeilDeSauronView } from "./components/OeilDeSauronView";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Skeleton } from "./components/ui/skeleton";
import { ProgressBar, tierOf } from "./components/ProgressBar";
import { fetchCollectionValue } from "./lib/api";
import { ModuleIcon } from "./components/ModuleIcon";

import { Sidebar } from "./components/Sidebar";
import { StatWidget } from "./components/StatWidget";
import { SetCard } from "./components/SetCard";
import { handleSetAssetError } from "./lib/images";
import { useSetAsset } from "./hooks/useSetAsset";
import { OwnedCardsView } from "./components/OwnedCardsView";
import { CollectionValueView } from "./components/CollectionValueView";
import { RandomMissingCard } from "./components/RandomMissingCard";
import { TopRisersCard } from "./components/TopRisersCard";
import { CardDetailModal } from "./components/CardDetailModal";
import { SetDetailView } from "./components/SetDetailView";
import { useMasterSetRules } from "./hooks/useMasterSetRules";
import { GlobalBrowseView } from "./components/GlobalBrowseView";
import { CrossRarityView } from "./components/CrossRarityView";
import { CustomListsView } from "./components/CustomListsView";
import { CustomVariantsView } from "./components/CustomVariantsView";
import { InvestmentView } from "./components/InvestmentView";
import { SettingsPanel } from "./components/SettingsPanel";
import { FriendCollectionView } from "./components/FriendCollectionView";
import { FriendListView } from "./components/FriendListView";
import { FriendsView } from "./components/FriendsView";
import { PriceAnomaliesView } from "./components/PriceAnomaliesView";
import { DebugView } from "./components/DebugView";
import { ScrollToTop } from "./components/ScrollToTop";
import { DebugOverlay } from "./components/DebugOverlay";
import { BottomNavBar } from "./components/BottomNavBar";
import { PokedexView } from "./components/PokedexView";
import { PokedexHomeCard } from "./components/PokedexHomeCard";
import { WantedView } from "./components/WantedView";
import { ScannerCapture } from "./components/ScannerCapture";

import { useCollection } from "./hooks/useCollection";
import { useFriendCollection } from "./hooks/useFriendCollection";
import { useSeriesData } from "./hooks/useSeriesData";
import { useCustomLists } from "./hooks/useCustomLists";
import { useCustomVariants } from "./hooks/useCustomVariants";
import { useRarityBalls } from "./hooks/useRarityBalls";
import { setRarityBallCache } from "./lib/balls";
import { useSidebarPrefs } from "./hooks/useSidebarPrefs";
import { useAuth } from "./hooks/useAuth";
import { Login } from "./components/Login";
import { buildSidebarBlocs } from "./lib/sidebar";
import { useScrollMemory } from "./hooks/useScrollMemory";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import { useNativePlatformClass } from "./hooks/useNativePlatformClass";
import { checkForAppUpdate } from "./lib/appUpdater";

// Petit composant dédié pour la vignette de la carte "Progression des
// collections" : useSetAsset est un Hook, il ne peut pas être appelé
// directement dans le callback d'un .map() (nombre d'appels variable selon
// la liste) — il lui faut son propre composant.
const SetSymbolIcon = ({ setId, symbol }) => {
  const src = useSetAsset(setId, "symbol", symbol);
  if (!src) return null;
  return <img src={src} alt="" className="w-6 h-6 object-contain" onError={handleSetAssetError} />;
};

function App() {
  const { user, status, onLoggedIn, logout } = useAuth();

  // Vérifie une fois au démarrage si un nouveau build a été publié (app
  // Android uniquement, no-op sur le web) — avant même le login, pour que
  // l'écran de connexion lui-même soit déjà à jour.
  useEffect(() => { checkForAppUpdate(); }, []);

  // Tant qu'on vérifie le token : écran neutre
  if (status === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0a0c", display: "flex",
        alignItems: "center", justifyContent: "center", color: "#52525b" }}>
        Chargement…
      </div>
    );
  }

  // Non connecté : écran de login
  if (status === "anonymous") {
    return <Login onLoggedIn={onLoggedIn} />;
  }

  // Connecté : l'app normale
  return <AppContent user={user} onLogout={logout} />;
}

function AppContent({ user, onLogout }) {
  const { seriesData, orderedSets, loading, refresh: refreshSeries, refreshing: refreshingSeries } = useSeriesData();
  const {
    collection, conditions, stats, completion, completionMap, completionCounts, setPreferences,
    setStatus, removeStatus, bulkMarkStatus, bulkRemoveStatus, updateSetMode,
    updateCondition, pendingWrites, syncingWrites, flushQueue, reloadCompletion,
  } = useCollection();
  const { rules: masterRules, addRule: addMasterRule, removeRule: removeMasterRule } = useMasterSetRules(reloadCompletion);
  const { lists, reloadLists, addList, updateListName, removeList, addToList, updateListPrivacy } = useCustomLists();
  const {
    variants: customVariants, applications: customApplications,
    reload: reloadCustomVariants, loadError: customVariantsError,
    addVariant, editVariant, removeVariant, applyVariant, removeApplication,
  } = useCustomVariants();
  const { rarityBalls, setRarityBallOverride, clearRarityBallOverride } = useRarityBalls();
  // Synchronise le cache module lu par ballForVariant (lib/balls.js) — évite
  // de faire passer `rarityBalls` en props à travers chaque vue qui dessine
  // une carte.
  useEffect(() => { setRarityBallCache(rarityBalls); }, [rarityBalls]);

  const { prefs: sidebarPrefs, savePrefs: saveSidebarPrefs } = useSidebarPrefs();

  // Nombre de cartes en hausse récente (tendance > moyenne 30j), pour le
  // pied de la tuile « Valeur estimée » (style B, 2026-09-28) — même appel
  // léger que celui déjà fait par TopRisersCard, juste le compte nous
  // intéresse ici, pas la liste.
  const [risersCount, setRisersCount] = useState(null);
  useEffect(() => {
    fetchCollectionValue().then(d => setRisersCount((d.risers || []).length)).catch(() => {});
  }, []);

  const [activeSetId, setActiveSetId] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const [showCrossView, setShowCrossView] = useState(false);
  const [showLists, setShowLists] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [showInvestment, setShowInvestment] = useState(false);
  // Onglet « Œil de Sauron » : toutes les cartes aux prix réels observés.
  const [showOeil, setShowOeil] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showPokedex, setShowPokedex] = useState(false);
  const [showWanted, setShowWanted] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  // Liste ordonnée des cartes affichées autour de la carte ouverte, pour la
  // navigation suivante/précédente dans le modal (null = pas de nav).
  const [cardSiblings, setCardSiblings] = useState(null);
  const [seriesSearch, setSeriesSearch] = useState("");
  // Filtre de la page d'accueil posé par la recherche globale (un bloc ou une
  // série) : { label, ids } — null = toutes les collections.
  const [setFilter, setSetFilter] = useState(null);
  const [browseIllustrator, setBrowseIllustrator] = useState("");
  const [browseName, setBrowseName] = useState("");
  // Recherche par Pokémon (Pokédex, nom d'une carte) : TCG Pocket est un jeu à
  // part, on l'écarte d'office. « Explorer les cartes » ouvert directement
  // continue, lui, de tout afficher.
  const [browseNoTcgp, setBrowseNoTcgp] = useState(false);
  // D'où l'on vient quand « Explorer les cartes » a été ouvert par rebond
  // (clic sur une espèce du Pokédex, sur un nom, sur un illustrateur) : sert
  // à proposer un vrai bouton retour au lieu de laisser l'utilisateur coincé.
  const [browseOrigin, setBrowseOrigin] = useState(null);
  // Région du Pokédex ouverte (National / Kanto / …) : levée ici plutôt que
  // dans PokedexView, sinon un aller-retour via "Retour au Pokédex" démonte
  // le composant et perd la région pour retomber sur l'écran de sélection.
  const [pokedexRegion, setPokedexRegion] = useState(null);
  const [showOnlyStarted, setShowOnlyStarted] = useState(false);
  const [showOwned, setShowOwned] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  // Vue en lecture seule de la collection d'un ami (cf. section "Amis" des
  // Réglages) : { friend_id, username } | null.
  const [viewingFriend, setViewingFriend] = useState(null);
  // Quelle collection de cet ami est actuellement ouverte en détail (lecture
  // seule) ; toujours remis à null quand on change d'ami ou qu'on referme la
  // vue amis (cf. closeAll).
  const [friendSetId, setFriendSetId] = useState(null);
  // Liste PARTAGÉE d'un ami actuellement ouverte (cf. FriendCollectionView) —
  // distincte de friendSetId : on ne peut pas avoir les deux à la fois.
  const [friendListId, setFriendListId] = useState(null);
  const {
    collection: friendCollection, conditions: friendConditions, setPreferences: friendSetPreferences,
  } = useFriendCollection(viewingFriend?.friend_id);
  const [showAnomalies, setShowAnomalies] = useState(false);

  // Lien profond depuis l'extension Œil de Sauron (bouton "Ouvrir dans
  // PokeTracker" sur une fiche Cardmarket, cf. extension/panel.js) :
  // ?card=<id>&set=<setId> ouvre directement la fiche AU-DESSUS de la
  // collection concernée (pas de l'accueil) - sinon fermer la fiche laissait
  // l'utilisateur à l'accueil sans repère, sans savoir dans quelle collection
  // il se trouvait (retour d'usage du 2026-08-15). Nettoyé de l'URL ensuite,
  // sinon un rafraîchissement ultérieur la rouvrirait à chaque fois.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cardId = params.get("card");
    if (!cardId) return;
    const setId = params.get("set");
    if (setId) setActiveSetId(setId);
    setSelectedCardId(cardId);
    params.delete("card");
    params.delete("set");
    const rest = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
  }, []);

  // Ouvre une carte dans le modal. `siblings` (optionnel) = liste ordonnée des
  // ids affichés autour, pour activer la navigation suivante/précédente.
  const openCard = (card, siblings) => {
    setSelectedCardId(card.id);
    setCardSiblings(Array.isArray(siblings) ? siblings : null);
  };

  const closeAll = (over = {}) => {
    setActiveSetId(over.activeSetId ?? null);
    setShowBrowse(!!over.showBrowse);
    setShowCrossView(!!over.showCrossView);
    setShowLists(!!over.showLists);
    setShowVariants(!!over.showVariants);
    setShowInvestment(!!over.showInvestment);
    setShowOeil(!!over.showOeil);
    setShowDebug(!!over.showDebug);
    setShowPokedex(!!over.showPokedex);
    setShowWanted(!!over.showWanted);
    setShowOwned(!!over.showOwned);
    setShowValue(!!over.showValue);
    setShowFriends(!!over.showFriends);
    setViewingFriend(over.viewingFriend ?? null);
    setFriendSetId(null);
    setFriendListId(null);
    setShowAnomalies(!!over.showAnomalies);
    // Toute navigation « normale » efface la provenance : le bouton retour ne
    // doit renvoyer vers le Pokédex que si c'est bien de là qu'on vient.
    if (!over.keepOrigin) setBrowseOrigin(null);
  };
  const goHome = () => { setSetFilter(null); closeAll(); };
  const openSet = (id) => closeAll({ activeSetId: id });
  const openFriend = (friend) => { setShowSettings(false); closeAll({ viewingFriend: friend }); };
  // Ouvrir/fermer une collection précise DE L'AMI EN COURS : ne passe pas par
  // closeAll (qui remettrait viewingFriend à zéro), on reste dans sa vue.
  const openFriendSet = (setId) => setFriendSetId(setId);
  const closeFriendSet = () => setFriendSetId(null);
  const openFriendList = (listId) => setFriendListId(listId);
  const closeFriendList = () => setFriendListId(null);
  const openAnomalies = () => { setShowSettings(false); closeAll({ showAnomalies: true }); };
  const openBrowse = () => { setBrowseNoTcgp(true); closeAll({ showBrowse: true }); };
  const openCrossView = () => closeAll({ showCrossView: true });
  const openLists = () => closeAll({ showLists: true });
  const openVariants = () => closeAll({ showVariants: true });
  const openInvestment = () => closeAll({ showInvestment: true });
  const openOeil = () => closeAll({ showOeil: true });
  const openDebug = () => { setShowSettings(false); closeAll({ showDebug: true }); };
  const openPokedex = () => closeAll({ showPokedex: true });
  const openWanted = () => closeAll({ showWanted: true });
  const openFriends = () => closeAll({ showFriends: true });
  const openOwned = () => closeAll({ showOwned: true });
  const openValue = () => closeAll({ showValue: true });

  // Grille d'accueil groupée par BLOC (Épée et Bouclier, EX, Kits du dresseur…),
  // comme la sidebar et comme Pokécardex : une seule grille à plat de plus de
  // 200 extensions ne dit pas où l'on est. On repart de `buildSidebarBlocs`
  // plutôt que de `orderedSets` pour respecter d'office l'ordre et les masquages
  // définis dans les Réglages — une seule source de vérité.
  // Le bloc « Autre » garde ses sous-entrées (POP, McDo, TCG Pocket…) : elles
  // deviennent des sections à part entière, sans quoi tout y serait mélangé.
  const filteredBlocs = useMemo(() => {
    const q = seriesSearch.trim().toLowerCase();
    const keep = (set) => {
      if (q && !set.name.toLowerCase().includes(q)) return false;
      if (setFilter && !setFilter.ids.has(set.id)) return false;
      if (showOnlyStarted && !(completionMap[set.id] > 0)) return false;
      return true;
    };
    const sections = [];
    buildSidebarBlocs(seriesData, sidebarPrefs).forEach(bloc => {
      const parts = bloc.subBlocs?.length
        ? bloc.subBlocs.map(sub => ({ key: `${bloc.key}-${sub.key}`, label: sub.label, sets: sub.sets }))
        : [{ key: bloc.key, label: bloc.label, sets: bloc.sets }];
      parts.forEach(part => {
        const sets = part.sets.filter(keep);
        if (sets.length) sections.push({ ...part, sets });
      });
    });
    return sections;
  }, [seriesData, sidebarPrefs, seriesSearch, setFilter, showOnlyStarted, completionMap]);

  const filteredSetCount = useMemo(
    () => filteredBlocs.reduce((n, b) => n + b.sets.length, 0),
    [filteredBlocs]
  );

  // ── Recherche globale : chaque type de résultat mène à la bonne vue ──
  const searchHandlers = {
    onSelectBloc: (bloc) => {
      setSetFilter({ label: bloc.label, ids: new Set(bloc.setIds) });
      closeAll();
    },
    onSelectSerie: (serie) => {
      const found = seriesData.find(s => s.id === serie.id);
      setSetFilter({ label: serie.name, ids: new Set((found?.sets || []).map(s => s.id)) });
      closeAll();
    },
    onSelectSet: (id) => { setSetFilter(null); openSet(id); },
    onSelectCard: (id) => { setCardSiblings(null); setSelectedCardId(id); },
    onSelectIllustrator: (name) => browseByIllustrator(name),
    // Une espèce mène à toutes ses cartes, TCG Pocket écarté (browseByName).
    onSelectSpecies: (name) => browseByName(name),
  };

  // Rebonds depuis la fiche carte vers "Explorer les cartes" : on ferme le
  // modal, sinon il resterait ouvert par-dessus la vue qu'on vient d'ouvrir.
  const browseByIllustrator = (name) => {
    setBrowseName("");
    setBrowseIllustrator(name);
    // TCG Pocket écarté par défaut ICI AUSSI : c'est un jeu à part, et ses
    // cartes polluaient les résultats quand on arrivait par un illustrateur.
    // Le bouton de la vue permet toujours de les réafficher.
    setBrowseNoTcgp(true);
    setSelectedCardId(null);
    closeAll({ showBrowse: true });
  };
  // `origin` = vue d'où part le rebond, pour offrir un retour (ex. depuis le
  // Pokédex : sans ça on ne pouvait plus revenir à la grille des espèces).
  const browseByName = (name, origin = null) => {
    setBrowseIllustrator("");
    setBrowseName(name);
    setBrowseNoTcgp(true);
    setSelectedCardId(null);
    closeAll({ showBrowse: true, keepOrigin: true });
    setBrowseOrigin(origin);
  };

  const BROWSE_ORIGINS = { pokedex: { label: "Retour au Pokédex", go: openPokedex } };

  // Progression globale : tous les sets VISIBLES dans la sidebar, sauf ceux
  // explicitement exclus dans les Réglages. Ce fonctionnement par exclusion
  // (et non par sélection) fait que la barre est utile dès qu'on l'active,
  // sans rien avoir à cocher — et un set masqué n'est jamais compté.
  // Le total vient du `cardCount` des sets (connu pour TOUS les sets), le
  // possédé de /collection/completion (qui ne liste que les sets entamés) —
  // un set jamais commencé compte donc bien dans le total, avec 0 possédée.
  const globalProgress = useMemo(() => {
    if (!sidebarPrefs.progressEnabled) return null;
    const excluded = new Set(sidebarPrefs.progressExcluded || []);
    const ownedBySet = {};
    completion.forEach(c => { ownedBySet[c.set_id] = c.owned_cards; });
    let owned = 0, total = 0, sets = 0;
    buildSidebarBlocs(seriesData, sidebarPrefs).forEach(bloc => bloc.sets.forEach(set => {
      if (excluded.has(set.id)) return;
      sets += 1;
      total += set.cardCount?.total || set.cardCount?.official || 0;
      owned += ownedBySet[set.id] || 0;
    }));
    if (total === 0) return null;
    return { owned, total, sets, pct: Math.round(owned / total * 100) };
  }, [sidebarPrefs, seriesData, completion]);

  // Clé de la vue affichée : sert de repère à la mémoire de défilement, pour
  // qu'un retour (extension -> accueil, fiche -> Pokédex) retombe où on était.
  const viewKey =
    showBrowse ? `browse:${browseName}|${browseIllustrator}` :
    showCrossView ? "cross" :
    showLists ? "lists" :
    showVariants ? "variants" :
    showPokedex ? "pokedex" :
    showWanted ? "wanted" :
    showOwned ? "owned" :
    showValue ? "value" :
    showOeil ? "oeil" :
    showDebug ? "debug" :
    showFriends ? "friends" :
    viewingFriend && friendListId ? `friend-list:${friendListId}` :
    viewingFriend && friendSetId ? `friend-set:${friendSetId}` :
    viewingFriend ? `friend:${viewingFriend.friend_id}` :
    showAnomalies ? "anomalies" :
    activeSetId ? `set:${activeSetId}` :
    "home";

  // Clé active pour la barre de navigation basse (mobile) : seulement les 4
  // entrées qu'elle contient, le reste (fiche, autres vues) ne surligne rien.
  const bottomNavActiveKey =
    mobileNavOpen ? "menu" :
    showBrowse ? "browse" :
    showPokedex ? "pokedex" :
    viewKey === "home" ? "home" :
    null;
  useScrollMemory(viewKey);
  useNativePlatformClass();

  // Bouton retour d'Android : on referme ce qui est ouvert, du plus superficiel
  // (fiche par-dessus tout) au plus profond (la vue elle-meme). Renvoyer false
  // = il n'y a plus rien a fermer, l'app peut quitter.
  useAndroidBackButton(() => {
    if (selectedCardId) { setSelectedCardId(null); return true; }
    if (showScanner) { setShowScanner(false); return true; }
    if (showSettings) { setShowSettings(false); return true; }
    if (mobileNavOpen) { setMobileNavOpen(false); return true; }
    // Une vue ouverte par rebond (espece du Pokedex -> Explorer) revient a sa
    // provenance, comme le bouton retour affiche a l'ecran.
    if (BROWSE_ORIGINS[browseOrigin]) { BROWSE_ORIGINS[browseOrigin].go(); return true; }
    // Le Pokedex a son propre niveau interne : une region ouverte revient
    // d'abord a la grille des regions.
    if (showPokedex && pokedexRegion) { setPokedexRegion(null); return true; }
    // Fiche d'un ami ouverte : revient d'abord à son aperçu, pas direct à l'accueil.
    if (viewingFriend && friendListId) { closeFriendList(); return true; }
    if (viewingFriend && friendSetId) { closeFriendSet(); return true; }
    if (viewKey !== "home") { goHome(); return true; }
    if (setFilter) { setSetFilter(null); return true; }
    return false;
  });

  // Le tiroir mobile (sidebar + raccourcis de nav, cf. Sidebar.jsx) se
  // referme tout seul dès qu'on navigue vers une nouvelle vue, plutôt que
  // d'ajouter un onClose manuel à chaque bouton un par un.
  useEffect(() => setMobileNavOpen(false), [viewKey]);

  // Raccourcis de nav dupliqués dans le tiroir mobile (Sidebar.jsx) : mêmes
  // actions que les boutons du header, invisibles sur mobile (hidden lg:flex).
  const mobileNavActions = [
    { key: "home", icon: Home, label: "Accueil", onClick: goHome },
    !showBrowse && { key: "browse", icon: Compass, label: "Explorer les cartes", onClick: openBrowse },
    !showPokedex && { key: "pokedex", icon: BookOpen, label: "Pokédex", onClick: openPokedex },
    { key: "scan", icon: ScanLine, label: "Scanner des cartes", onClick: () => { setMobileNavOpen(false); setShowScanner(true); } },
    !showCrossView && { key: "cross", icon: SlidersHorizontal, label: "Explorer par rareté", onClick: openCrossView },
    !showLists && { key: "lists", icon: ListPlus, label: "Mes listes", onClick: openLists },
    !showVariants && { key: "variants", icon: Sparkles, label: "Variantes perso", onClick: openVariants },
    !showFriends && { key: "friends", icon: Users, label: "Amis", onClick: openFriends },
    // Investissement : réservé aux comptes admin (Sange), invisible sinon —
    // ni dans le header desktop ni dans ce tiroir mobile.
    user?.is_admin && !showInvestment && { key: "investment", icon: TrendingUp, label: "Investissement", onClick: openInvestment },
    !showOeil && { key: "oeil", icon: Eye, label: "Œil de Sauron", onClick: openOeil },
    // setShowSettings ne change pas viewKey (Réglages n'est pas une "vue" au
    // même titre que les autres) : le useEffect qui referme le tiroir au
    // changement de vue ne se déclenche pas ici, donc on referme à la main
    // - sinon le tiroir reste ouvert PAR-DESSUS la fenêtre Réglages (qui,
    // elle, est en dessous en z-index), la rendant inaccessible au clic.
    { key: "settings", icon: Settings, label: "Réglages", onClick: () => { setShowSettings(true); setMobileNavOpen(false); } },
    { key: "logout", icon: LogOut, label: "Se déconnecter", onClick: onLogout },
  ].filter(Boolean);

  // Blocs pour le panneau Réglages : ordre de l'utilisateur mais SANS retirer
  // les blocs masqués — sinon on ne pourrait plus les réafficher.
  const settingsBlocs = useMemo(
    () => buildSidebarBlocs(seriesData, {
      hidden: [], hiddenSets: [],
      order: sidebarPrefs.order, setOrder: sidebarPrefs.setOrder,
    }),
    [seriesData, sidebarPrefs.order, sidebarPrefs.setOrder]
  );

  // Onglets de la barre du haut (proposition A validée le 2026-10-03) : tous
  // visibles en permanence, l'actif souligné — on sait toujours où l'on est.
  // Sur mobile, les mêmes actions vivent dans le tiroir (mobileNavActions).
  // 2026-10-03 (retour utilisateur) : « Explorer » et « Raretés » fusionnés
  // en un seul onglet avec une bascule dans la vue ; « Variantes » retiré
  // des onglets (gestion locale à une carte/un set), accessible via le menu
  // de la pastille utilisateur.
  const navTabs = [
    { key: "home", label: "Accueil", icon: Home, active: viewKey === "home" || viewKey.startsWith("set:"), onClick: goHome },
    { key: "browse", label: "Explorer", icon: Compass, active: showBrowse || showCrossView, onClick: openBrowse },
    { key: "pokedex", label: "Pokédex", icon: BookOpen, active: showPokedex, onClick: openPokedex },
    { key: "lists", label: "Listes", icon: ListPlus, active: showLists, onClick: openLists },
    { key: "friends", label: "Amis", icon: Users, active: showFriends || !!viewingFriend, onClick: openFriends },
    // Investissement : réservé aux comptes admin (Sange).
    user?.is_admin && { key: "investment", label: "Invest.", icon: TrendingUp, active: showInvestment, onClick: openInvestment, tone: "admin" },
    { key: "oeil", label: "Œil", icon: Eye, active: showOeil, onClick: openOeil, tone: "admin" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-zinc-950">
      <TopNav tabs={navTabs} user={user} avatar={sidebarPrefs.avatar} onHome={goHome}
        stats={{ cards: stats.owned_unique, sets: globalProgress?.sets ?? completion.length, value: stats.estimated_value }}
        menuItems={[
          { key: "scan", label: "Scanner des cartes", icon: ScanLine, onClick: () => setShowScanner(true) },
          { key: "variants", label: "Variantes perso", icon: Sparkles, onClick: openVariants },
        ]}
        onMenu={() => setMobileNavOpen(v => !v)}
        onSettings={() => setShowSettings(true)} onLogout={onLogout}
        searchHandlers={searchHandlers} />

      <div className="app-layout">
        {loading ? (
          <div className={`sidebar flex flex-col gap-2 p-3 ${mobileNavOpen ? "mobile-open" : ""}`}>
            {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-6 bg-zinc-800 rounded" />)}
          </div>
        ) : (
          <Sidebar
            seriesData={seriesData}
            activeSetId={activeSetId}
            onSelectSet={openSet}
            completionMap={completionMap}
            completionCounts={completionCounts}
            sidebarPrefs={sidebarPrefs}
            mobileOpen={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            navActions={mobileNavActions}
          />
        )}

        <main className="main-content">
          {/* Explorer : une seule destination, deux façons de parcourir
              (par carte = recherche libre, par rareté = sets × raretés). */}
          {(showBrowse || showCrossView) && (
            <div className="explore-modes" role="tablist" aria-label="Mode d'exploration">
              <button className={`explore-mode ${showBrowse ? "on" : ""}`} onClick={openBrowse} role="tab" aria-selected={showBrowse}>
                <Compass className="w-4 h-4" />Par carte
              </button>
              <button className={`explore-mode ${showCrossView ? "on" : ""}`} onClick={openCrossView} role="tab" aria-selected={showCrossView}>
                <SlidersHorizontal className="w-4 h-4" />Par rareté
              </button>
            </div>
          )}
          {showBrowse ? (
            <GlobalBrowseView
              key={`browse-${browseName}-${browseIllustrator}-${browseNoTcgp}`}
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
              lists={lists}
              onCreateList={addList}
              onAddToList={addToList}
              initialIllustrator={browseIllustrator}
              initialName={browseName}
              initialExcludeTcgp={browseNoTcgp}
              backLabel={BROWSE_ORIGINS[browseOrigin]?.label}
              onBack={BROWSE_ORIGINS[browseOrigin]?.go}
            />
          ) : showCrossView ? (
            <CrossRarityView
              seriesData={seriesData}
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
              lists={lists}
              onCreateList={addList}
              onAddToList={addToList}
            />
          ) : showLists ? (
            <CustomListsView
              lists={lists}
              onCreateList={addList}
              onRenameList={updateListName}
              onDeleteList={removeList}
              onReloadLists={reloadLists}
              onTogglePrivacy={updateListPrivacy}
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
            />
          ) : showVariants ? (
            <CustomVariantsView
              variants={customVariants}
              applications={customApplications}
              loadError={customVariantsError}
              onReload={reloadCustomVariants}
              onCreate={addVariant}
              onEdit={editVariant}
              onRemove={removeVariant}
              onRemoveApplication={removeApplication}
            />
          ) : showInvestment ? (
            user?.is_admin ? <InvestmentView /> : null
          ) : showPokedex ? (
            <PokedexView
              region={pokedexRegion}
              onRegionChange={setPokedexRegion}
              onSearchSpecies={(name) => browseByName(name, "pokedex")}
            />
          ) : showWanted ? (
            <WantedView
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
            />
          ) : showOwned ? (
            <OwnedCardsView
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
            />
          ) : showValue ? (
            <CollectionValueView
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
            />
          ) : showOeil ? (
            <OeilDeSauronView onCardClick={openCard} seriesData={seriesData} sidebarPrefs={sidebarPrefs} />
          ) : showDebug ? (
            <DebugView onSelectCard={(id) => setSelectedCardId(id)} />
          ) : showFriends ? (
            <FriendsView onViewFriend={openFriend} />
          ) : viewingFriend && friendListId ? (
            <FriendListView
              key={`friend-list-${friendListId}`}
              listId={friendListId}
              friendId={viewingFriend.friend_id}
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
            />
          ) : viewingFriend && friendSetId ? (
            <SetDetailView
              key={`friend-set-${friendSetId}`}
              setId={friendSetId}
              onBack={closeFriendSet}
              collection={friendCollection}
              conditions={friendConditions}
              mode={friendSetPreferences[friendSetId] || "normal"}
              onCardClick={openCard}
              readOnly
            />
          ) : viewingFriend ? (
            <FriendCollectionView friendId={viewingFriend.friend_id} username={viewingFriend.username}
              onOpenSet={openFriendSet} onOpenList={openFriendList} />
          ) : showAnomalies ? (
            <PriceAnomaliesView onCardClick={openCard} />
          ) : activeSetId ? (
            <SetDetailView
              setId={activeSetId}
              onBack={goHome}
              collection={collection}
              conditions={conditions}
              onStatusChange={setStatus}
              onRemove={removeStatus}
              onCardClick={openCard}
              onBulkMarkStatus={bulkMarkStatus}
              onBulkRemove={bulkRemoveStatus}
              mode={setPreferences[activeSetId] || "normal"}
              onModeChange={(mode) => updateSetMode(activeSetId, mode)}
              lists={lists}
              onCreateList={addList}
              onAddToList={addToList}
              customVariants={customVariants}
              customApplications={customApplications}
              onCreateCustomVariant={addVariant}
              onApplyCustomVariant={applyVariant}
              onRemoveCustomApplication={removeApplication}
              masterRules={masterRules}
              onAddMasterRule={addMasterRule}
              onRemoveMasterRule={removeMasterRule}
            />
          ) : (
            <>
              {/* Disposition d'après le croquis fourni par l'utilisateur : une
                  colonne étroite de 3 compteurs empilés à gauche, une tuile
                  « Il te manque » à droite (variante `tall` de
                  RandomMissingCard). Le grid CSS étire par défaut
                  (align-items: stretch) : les tuiles prennent automatiquement
                  la hauteur de la colonne de gauche.
                  Retour utilisateur (2026-07-27) : la tuile « Il te manque »
                  s'étirait sur TOUTE la largeur restante avec une image
                  minuscule dedans → colonne à largeur FIXE (plus étroite,
                  image agrandie dans le composant), et le vide libéré à droite
                  est comblé par « En hausse récente » plutôt que de laisser
                  un simple espace décoratif. */}
              <div className="grid grid-cols-1 lg:grid-cols-[280px_480px_1fr] gap-4 mb-8">
                {/* Trois compteurs cote a cote sur un telephone rognaient leurs
                    libelles (« CARTES POSSÉDÉ », « RECHERC ») : ils s'empilent
                    en pleine largeur en dessous de `sm`. */}
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2 lg:gap-4">
                  <StatWidget icon={CheckCircle2} label="Cartes possédées" value={stats.owned_unique}
                    subtext={`${stats.owned_total} au total`} color="bg-emerald-500" compact
                    footerMeta={<><b>{globalProgress?.sets ?? completion.length}</b> collections suivies</>}
                    footerLabel="Voir" onClick={openOwned} />
                  <StatWidget icon={Star} label="Recherchées" value={stats.wanted_count}
                    subtext="Sur votre liste" color="bg-blue-500" compact
                    footerMeta={stats.wanted_count > 0 ? "Toutes variantes confondues" : "Aucune carte pour l'instant"}
                    footerLabel={stats.wanted_count > 0 ? "Voir la liste" : "Explorer"}
                    onClick={stats.wanted_count > 0 ? openWanted : openBrowse} />
                  <StatWidget icon={TrendingUp} label="Valeur estimée"
                    value={stats.estimated_value > 0 ? stats.estimated_value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "—"}
                    valueSuffix={stats.estimated_value > 0 ? `,${stats.estimated_value.toFixed(2).split(".")[1]} €` : null}
                    subtext={stats.owned_unique > 0 && stats.estimated_value > 0
                      ? `Prix tendance Cardmarket · ${(stats.estimated_value / stats.owned_unique).toFixed(2)} € / carte`
                      : "Prix tendance Cardmarket"}
                    color="bg-red-500" compact
                    footerMeta={risersCount > 0 ? <>↗ <b>{risersCount}</b> cartes en hausse récente</> : ""}
                    footerLabel="Classement" onClick={openValue} />
                </div>
                <RandomMissingCard tall onCardClick={openCard} />
                {/* « En hausse récente » ne rentre pas sur un ecran de telephone
                    (vignettes rognees) et n'est pas ce qu'on vient chercher a
                    l'ouverture de l'appli : reserve au desktop, le classement
                    complet restant accessible par la tuile « Valeur estimée ». */}
                <div className="hidden lg:block">
                  <TopRisersCard onCardClick={openCard} onSeeAll={openValue} />
                </div>
              </div>

              {sidebarPrefs.pokedexHome && <PokedexHomeCard onOpen={openPokedex} />}

              {globalProgress && (
                <div className="mb-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                  <div className="flex items-baseline justify-between mb-3">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <ModuleIcon icon={BarChart3} color="bg-sky-500" size="lg" />
                      Progression globale
                      <span className="text-xs font-normal text-zinc-500">
                        {globalProgress.sets} collection{globalProgress.sets > 1 ? "s" : ""} suivie{globalProgress.sets > 1 ? "s" : ""}
                      </span>
                    </h2>
                    <span className="text-sm text-zinc-400">
                      <span className="text-white font-semibold">{globalProgress.owned.toLocaleString("fr-FR")}</span>
                      <span className="text-zinc-600">/{globalProgress.total.toLocaleString("fr-FR")}</span>
                    </span>
                  </div>
                  <ProgressBar size="lg" pct={globalProgress.pct} owned={globalProgress.owned} total={globalProgress.total} showCount={false} />
                </div>
              )}

              {completion.length > 0 && (
                <div className="mb-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                    <ModuleIcon icon={Trophy} color="bg-amber-500" size="lg" />
                    Progression des collections
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {completion.slice(0, 6).map(set => (
                      <button key={set.set_id} className={`set-completion-card set-completion-tier-${tierOf(set.completion_percentage)} text-left`} onClick={() => openSet(set.set_id)}>
                        <div className="flex items-center gap-3 mb-3">
                          {set.set_symbol && (
                            <div className="set-completion-symbol">
                              <SetSymbolIcon setId={set.set_id} symbol={set.set_symbol} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-white truncate">{set.set_name}</h4>
                            <p className="text-xs text-zinc-500 truncate">{set.set_series}</p>
                          </div>
                        </div>
                        <ProgressBar size="md" pct={set.completion_percentage} owned={set.owned_cards} total={set.total_cards} />
                        <div className="text-right mt-2 text-xs text-zinc-500">
                          {set.total_cards - set.owned_cards} manquante{set.total_cards - set.owned_cards > 1 ? "s" : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                  {completion.length > 6 && (
                    <div className="text-center mt-4 text-sm text-zinc-500">+ {completion.length - 6} autres collections en cours</div>
                  )}
                </div>
              )}

              <div className="mb-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-red-500" />Toutes les collections
                  </h2>
                  <Button variant="outline" size="sm"
                    className={`border-zinc-700 text-white ${showOnlyStarted ? "bg-zinc-800" : ""}`}
                    onClick={() => setShowOnlyStarted(!showOnlyStarted)}>
                    {showOnlyStarted ? "Toutes les collections" : "Mes collections en cours"}
                  </Button>
                </div>
                <div className="relative mb-5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input placeholder="Rechercher une collection..." className="search-input pl-10"
                    value={seriesSearch} onChange={(e) => setSeriesSearch(e.target.value)} />
                </div>
                {/* Filtre posé par la recherche globale (bloc ou série) */}
                {setFilter && (
                  <div className="flex items-center gap-2 mb-5">
                    <span className="text-sm text-zinc-400">Filtré sur</span>
                    <button onClick={() => setSetFilter(null)}
                      title="Retirer le filtre"
                      className="flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-full px-3 py-1">
                      {setFilter.label}<X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {loading ? (
                  <div className="sets-grid">
                    {[...Array(12)].map((_, i) => <Skeleton key={i} className="h-48 bg-zinc-900 rounded-xl" />)}
                  </div>
                ) : filteredSetCount === 0 ? (
                  <div className="text-center py-16 text-zinc-500">
                    <Library className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune collection trouvée.</p>
                  </div>
                ) : (
                  filteredBlocs.map(bloc => (
                    <section key={bloc.key} className="mb-9 home-bloc-section">
                      <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        {bloc.label}
                        <span className="text-zinc-600 normal-case font-normal">{bloc.sets.length}</span>
                      </h3>
                      <div className="sets-grid">
                        {bloc.sets.map(set => (
                          <SetCard key={set.id} set={set} completion={completionMap[set.id]} counts={completionCounts[set.id]} onSelect={(s) => openSet(s.id)} />
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Lecture seule UNIQUEMENT en consultant une COLLECTION d'ami
          (friendSetId) — dans sa liste PARTAGÉE, c'est volontairement TA
          propre collection qui s'affiche et reste modifiable (le but est
          de voir/marquer ce que TU as parmi ce qu'il recherche). */}
      <CardDetailModal cardId={selectedCardId} isOpen={!!selectedCardId}
        siblingIds={cardSiblings} onNavigate={(id) => setSelectedCardId(id)}
        onClose={() => setSelectedCardId(null)}
        collection={viewingFriend && friendSetId ? friendCollection : collection}
        conditions={viewingFriend && friendSetId ? friendConditions : conditions}
        readOnly={!!(viewingFriend && friendSetId)}
        onStatusChange={setStatus} onRemove={removeStatus} onUpdateCondition={updateCondition}
        lists={lists} onCreateList={addList} onAddToList={addToList}
        customVariants={customVariants} customApplications={customApplications}
        onCreateCustomVariant={addVariant} onApplyCustomVariant={applyVariant}
        onRemoveCustomApplication={removeApplication}
        customVariantsError={customVariantsError} onReloadCustomVariants={reloadCustomVariants}
        masterRules={masterRules} onAddMasterRule={addMasterRule} onRemoveMasterRule={removeMasterRule}
        onBrowseName={browseByName} onBrowseIllustrator={browseByIllustrator}
        onOpenOeil={openOeil} />

      <ScrollToTop />

      <DebugOverlay />

      <BottomNavBar activeKey={bottomNavActiveKey}
        onHome={goHome} onMenu={() => setMobileNavOpen(v => !v)}
        onBrowse={openBrowse} onPokedex={openPokedex}
        onScan={() => setShowScanner(true)} />

      {/* Scanner : caméra en direct dans l'appli, choix d'une photo sur le
          site. Masqué (mais gardé monté, résultats compris) tant qu'une
          fiche ouverte depuis ses résultats est affichée. */}
      {showScanner && (
        <ScannerCapture open={showScanner} suspended={!!selectedCardId}
          onClose={() => setShowScanner(false)}
          onOpenCard={(cardId) => openCard({ id: cardId })}
          collection={collection} onSetStatus={setStatus} />
      )}

      <SettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)}
        blocs={settingsBlocs} prefs={sidebarPrefs} onSavePrefs={saveSidebarPrefs}
        onOpenDebug={openDebug}
        onRefreshSeries={refreshSeries} refreshingSeries={refreshingSeries}
        pendingWrites={pendingWrites} syncingWrites={syncingWrites} onFlushWrites={flushQueue}
        onOpenAnomalies={openAnomalies}
        rarityBalls={rarityBalls} onSetRarityBall={setRarityBallOverride}
        onClearRarityBall={clearRarityBallOverride} />
    </div>
  );
}

export default App;
